/* ==========================================================================
   F32 — Sticky header (prototype)
   --------------------------------------------------------------------------
   The sticking itself is pure CSS (sticky-header.css). This file only does
   the two things CSS cannot:

   1. Publishes the real height of the bar as --jj-header-h, so anchor
      targets and the page's other sticky elements clear it even if the logo
      or the menu changes. Writing a custom property costs no layout on its
      own — the properties that read it (scroll-padding, sticky `top`) do not
      move anything in flow.

   2. Toggles .jj-stuck on the header once the page has scrolled past the
      top, using an IntersectionObserver on a zero-flow sentinel. No scroll
      listener, so nothing runs per scroll frame.

   If this file fails to load or the browser is too old for either observer,
   the header still sticks — it just never compacts. Nothing here is required
   for the layout to be correct.
   ========================================================================== */
(function () {
  'use strict';

  var header = document.querySelector('.site-header');
  if (!header) return;

  var row = header.querySelector('.site-header__row') || header;

  /* On the homepage the header is absolutely positioned inside
     .header-overlay-wrap and it is the WRAP that sticks, so that is what the
     sentinel has to be measured against. Everywhere else it is the header. */
  var stickyRoot = header.closest('.header-overlay-wrap') || header;

  /* ---------------------------------------------------------------------
     1. --jj-header-h
     ---------------------------------------------------------------------
     Measure the ROW, not the <header>: the mobile menu lives inside the
     header, and an open menu would otherwise report a bar several hundred
     pixels tall and push every sticky toolbar off the screen. +1 for the
     header's 1px bottom border.
     --------------------------------------------------------------------- */
  var lastHeight = 0;
  var frameQueued = false;

  function publishHeight() {
    frameQueued = false;
    var height = Math.round(row.getBoundingClientRect().height) + 1;
    if (!height || height === lastHeight) return;
    lastHeight = height;
    document.documentElement.style.setProperty('--jj-header-h', height + 'px');
  }

  /* The compaction transition resizes the row ~every frame for 180ms, so
     coalesce to one write per frame rather than one per observer callback. */
  function scheduleHeight() {
    if (frameQueued) return;
    frameQueued = true;
    (window.requestAnimationFrame || window.setTimeout)(publishHeight, 16);
  }

  if (window.ResizeObserver) {
    new window.ResizeObserver(scheduleHeight).observe(row);
  } else {
    window.addEventListener('resize', scheduleHeight, { passive: true });
  }
  publishHeight();

  /* Logos are eager but not necessarily decoded when this runs, and the row
     height is driven by the logo. Re-measure once they settle. */
  Array.prototype.forEach.call(
    header.querySelectorAll('img'),
    function (img) {
      if (!img.complete) img.addEventListener('load', scheduleHeight, { once: true });
    }
  );
  window.addEventListener('load', scheduleHeight, { once: true });

  /* ---------------------------------------------------------------------
     2. .jj-stuck
     --------------------------------------------------------------------- */
  if (!('IntersectionObserver' in window)) return;

  var sentinel = document.createElement('div');
  sentinel.className = 'jj-header-sentinel';
  sentinel.setAttribute('aria-hidden', 'true');
  stickyRoot.parentNode.insertBefore(sentinel, stickyRoot);

  new IntersectionObserver(
    function (entries) {
      /* The sentinel sits exactly where the header pins, so "sentinel has
         left the top of the viewport" is the same instant as "header is
         stuck". */
      header.classList.toggle('jj-stuck', !entries[entries.length - 1].isIntersecting);
    },
    { threshold: 0 }
  ).observe(sentinel);
})();
